/**
 * Browser client for saved customer cards. Nothing here ever sends or receives a
 * card token — the server reads it itself (see ./index.ts).
 */

import request from '../request';
import type { AddDocumentBody } from '../vouchers/context/context';
import type { Voucher } from '../vouchers/types';
import type { SavedCardChargeStatus, SavedCustomerCard, TravelFileSavedCards } from './cardHelpers';
import { SPLIT_MIN_DAYS_BEFORE_TRIP } from '../pelecardUtils/pelecardInstallments';

/**
 * Longer than the server's own 60s gateway timeout (TOKEN_DEBIT_TIMEOUT_MS), and
 * shorter than the 300s ALB idle timeout in front of DEV and PROD. If the browser
 * gave up first, an approved charge would look like a failure and invite a
 * second one — which is why a timeout here maps to UNKNOWN, never REFUSED.
 */
const CHARGE_TIMEOUT_MS = 120_000;
/** The server retries briefly while the payment capture lands. */
const SAVE_TIMEOUT_MS = 30_000;

export const getPassengerSavedCards = async (passengerId: number | string): Promise<SavedCustomerCard[]> => {
    const res = await request.get(`/customer-cards/passenger/${passengerId}`);
    return res.data?.cards ?? [];
};

export const getTravelFileSavedCards = async (travelFileId: number | string): Promise<TravelFileSavedCards> => {
    const res = await request.get(`/customer-cards/travel-file/${travelFileId}`);
    return {
        passengers: res.data?.passengers ?? [],
        cards: res.data?.cards ?? [],
        tripDate: res.data?.tripDate ?? null,
        // Always sent by the server; the default only covers a malformed answer.
        fullPaymentWindowDays: Number.isFinite(res.data?.fullPaymentWindowDays)
            ? Number(res.data.fullPaymentWindowDays)
            : SPLIT_MIN_DAYS_BEFORE_TRIP,
        bookedThroughAgent: res.data?.bookedThroughAgent === true
    };
};

export type PaymentPlanInfo = {
    tripDate: string | null;
    fullPaymentWindowDays: number;
};

/** tripDate/fullPaymentWindowDays for a travel file, without the `customer_card:view`
 *  gate `getTravelFileSavedCards` carries — used by the new-card (Masleka) flow,
 *  which needs the installment/credit-deal plan without ever touching card data. */
export const getPaymentPlanInfo = async (travelFileId: number | string): Promise<PaymentPlanInfo> => {
    const res = await request.get(`/payment-plan-info/travel-file/${travelFileId}`);
    return {
        tripDate: res.data?.tripDate ?? null,
        fullPaymentWindowDays: Number.isFinite(res.data?.fullPaymentWindowDays)
            ? Number(res.data.fullPaymentWindowDays)
            : SPLIT_MIN_DAYS_BEFORE_TRIP
    };
};

export const deleteSavedCard = async (cardId: number): Promise<void> => {
    await request.delete(`/customer-cards/${cardId}`);
};

export type SaveCardBody = {
    /** The Pelecard transaction the receipt was paid with (paymentMethodObject.reference). */
    pelecardTransactionId: string;
    passengerId: number | string;
    travelFileId: number | string;
    /**
     * Required by the route. The receipt forms always send it: a card paid on the
     * payment page for a passenger payer is saved automatically (2026-09-16).
     */
    consent: true;
};

export const saveCardFromPayment = async (
    body: SaveCardBody
): Promise<{ card: SavedCustomerCard; alreadySaved: boolean }> => {
    const res = await request.post('/customer-cards/save', body, { timeout: SAVE_TIMEOUT_MS });
    return res.data;
};

export type ChargeSavedCardBody = {
    cardId: number;
    idempotencyKey: string;
    travelFileId: number | string;
    /** The passenger paying the receipt. The server refuses a card that is not theirs. */
    payerPassengerId: number | string;
    paymentsTerminalId?: number | string | null;
    classId?: unknown;
    /** Plain instalments; must be one of selectablePlainInstallmentCounts, or the server answers 400. */
    numberOfPayments?: number | null;
    cvv2?: string;
    holderIdNumber?: string;
    document: AddDocumentBody;
    /** The agent confirmed charging more than the travel file still owes. */
    allowOverBalance?: boolean;
};

/** An earlier attempt the server told us about (a duplicate key, or one blocking this charge). */
export type ChargeAttemptInfo = {
    chargeId: number;
    attemptStatus: 'PENDING' | 'SUCCESS' | 'FAILED' | 'UNKNOWN';
    approvalNumber: string | null;
    documentId: number | null;
    documentNumber: number | null;
};

export type ChargeSavedCardResult =
    | { status: 'SUCCESS'; chargeId: number; document: Voucher }
    | {
          status:
              | Exclude<SavedCardChargeStatus, 'SUCCESS'>
              | 'CHARGED_NO_RECEIPT'
              | 'DUPLICATE'
              | 'UNRESOLVED_ATTEMPT'
              | 'REFUSED';
          message: string;
          statusCode?: string;
          /** REFUSED only: `EXCEEDS_BALANCE` when the amount is over the travel
           *  file's balance — the dialog may then ask to charge anyway. */
          code?: string;
          amount?: number;
          balance?: number;
          paid?: number;
          currency?: string;
          chargeId?: number;
          approvalNumber?: string | null;
          attempt?: ChargeAttemptInfo;
      };

const SERVER_OUTCOMES = new Set(['FAILED', 'UNKNOWN', 'CHARGED_NO_RECEIPT', 'DUPLICATE', 'UNRESOLVED_ATTEMPT']);

const NO_ANSWER_MESSAGE =
    'No readable answer from the server — the charge may or may not have gone through. Do not retry — check ' +
    'the travel file’s receipts and the charge log.';

/**
 * Charge a saved card and issue the receipt. Never throws for an HTTP answer:
 * every outcome comes back as a value the form has to handle —
 *
 *   FAILED                 Pelecard declined; nothing moved, another try is safe
 *   UNKNOWN                may have charged; do NOT retry
 *   CHARGED_NO_RECEIPT     charged, no receipt; do NOT retry
 *   DUPLICATE              this key was used before; `attempt` says how it ended
 *   UNRESOLVED_ATTEMPT     an earlier attempt on this card / file needs a human
 *   REFUSED                our own validation said no (a 4xx) before any claim
 *
 * Anything we cannot read — no response, a timeout, a 5xx or 504 from a proxy, an
 * HTML error page, a 2xx without a receipt — is UNKNOWN, never REFUSED: the
 * request may have reached the server and charged the card.
 */
export const chargeSavedCardForReceipt = async (body: ChargeSavedCardBody): Promise<ChargeSavedCardResult> => {
    try {
        const res = await request.post('/customer-cards/charge', body, { timeout: CHARGE_TIMEOUT_MS });
        const data = res?.data;
        if (data && typeof data === 'object' && data.status === 'SUCCESS' && data.document) return data;
        return { status: 'UNKNOWN', message: NO_ANSWER_MESSAGE };
    } catch (err: any) {
        const response = err?.response;
        const data = response?.data;
        const isJsonObject = !!data && typeof data === 'object' && !Array.isArray(data);
        if (isJsonObject && SERVER_OUTCOMES.has(data.status)) return data;
        if (!response || Number(response.status) >= 500 || !isJsonObject) {
            return { status: 'UNKNOWN', message: NO_ANSWER_MESSAGE };
        }
        return {
            status: 'REFUSED',
            message: data?.message || 'The charge was refused.',
            ...(data?.code
                ? {
                      code: data.code,
                      amount: data.amount,
                      balance: data.balance,
                      paid: data.paid,
                      currency: data.currency
                  }
                : {})
        };
    }
};

/** A fresh idempotency key — one per receipt-dialog opening. */
export const newChargeAttemptKey = (): string =>
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
        ? (crypto as any).randomUUID()
        : `charge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
